import React, { useMemo, useState } from 'react';
import { Quote, QuoteFollowUpOutcome } from '../../types';
import { Button } from '../Button';
import { Check, Loader2, X, Clock, User, MessageSquare } from 'lucide-react';
import { FOLLOW_UP_OUTCOMES, FollowUpInput, nextFollowUpFromDays } from '../../services/quoteFollowUp';

const fmtDate = (v?: string) => (v ? new Date(v).toLocaleDateString('de-DE') : '—');
const todayIso = () => new Date().toISOString().slice(0, 10);

type NextMode = 'days' | 'date' | 'none';

interface Props {
  quote: Quote;
  customerName: string;
  /** Name des angemeldeten Nutzers (Vorbelegung "Nachgefasst von"). */
  actorName: string;
  actorId?: string;
  /** Grundeinstellung des Nachfass-Abstands (quoteConfig.followUpDays). */
  defaultIntervalDays: number;
  saving?: boolean;
  onCancel: () => void;
  onSubmit: (input: FollowUpInput) => void;
}

const QUICK_DAYS = [7, 14, 30];
const NEXT_MODES: [NextMode, string][] = [['days', 'In X Tagen'], ['date', 'Am Datum'], ['none', 'Kein weiteres']];

/**
 * Erfassung eines erledigten Nachfassens: Stand, Nachfassender, naechster
 * Termin. Bewusst ein Pflichtdialog statt eines Direkt-Klicks — "Erledigt"
 * ohne Stand war genau die Luecke, wegen der beim naechsten Anruf niemand
 * wusste, was beim letzten herauskam.
 */
export const QuoteFollowUpDoneModal: React.FC<Props> = ({
  quote, customerName, actorName, actorId, defaultIntervalDays, saving, onCancel, onSubmit,
}) => {
  const fallbackDays = defaultIntervalDays > 0 ? defaultIntervalDays : 14;
  const [outcome, setOutcome] = useState<QuoteFollowUpOutcome>('reached');
  const [note, setNote] = useState('');
  const [byName, setByName] = useState(actorName);
  const [mode, setMode] = useState<NextMode>('days');
  const [days, setDays] = useState<number>(fallbackDays);
  const [date, setDate] = useState<string>(nextFollowUpFromDays(todayIso(), fallbackDays));

  // "Kein weiteres Nachfassen" als Ergebnis und als Terminwahl sind dieselbe
  // Aussage — die Auswahl zieht die andere Seite mit, damit kein abgeschlossenes
  // Angebot doch wieder einen Termin bekommt (und umgekehrt).
  const chooseOutcome = (key: QuoteFollowUpOutcome) => {
    setOutcome(key);
    if (key === 'closed') setMode('none');
    else if (mode === 'none') setMode('days');
  };

  const chooseMode = (key: NextMode) => {
    setMode(key);
    if (key === 'none') setOutcome('closed');
    else if (outcome === 'closed') setOutcome('reached');
  };

  const nextDate = useMemo(() => {
    if (mode === 'none') return undefined;
    if (mode === 'date') return date || undefined;
    return nextFollowUpFromDays(todayIso(), days);
  }, [mode, date, days]);

  const noteOk = note.trim().length > 0;
  const nameOk = byName.trim().length > 0;
  const dateOk = mode !== 'date' || Boolean(date);
  const canSave = noteOk && nameOk && dateOk && !saving;

  const submit = () => {
    if (!canSave) return;
    onSubmit({
      outcome,
      note,
      // Die Nutzer-Id nur mitschreiben, wenn der vorbelegte Name stehen blieb:
      // wird ein Kollege eingetragen, ist die eigene Id die falsche Person.
      by: byName.trim() === actorName.trim() ? actorId : undefined,
      byName: byName.trim(),
      nextDate,
      nextIntervalDays: mode === 'days' ? days : undefined,
    });
  };

  const inputCls = 'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all';
  const labelCls = 'block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5';

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-in max-h-[92vh] flex flex-col">
        <div className="bg-slate-900 text-white p-6 flex justify-between items-start shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-amber-500/20 p-2 rounded-xl border border-amber-500/30"><Check className="w-6 h-6 text-amber-400" /></div>
            <div className="min-w-0">
              <h3 className="font-bold text-lg">Nachfassen erledigt</h3>
              <p className="text-[11px] text-slate-300 truncate">
                {quote.quoteNumber} · {customerName} · fällig war {fmtDate(quote.followUpDate)}
              </p>
            </div>
          </div>
          <button onClick={onCancel} className="p-1 text-slate-400 hover:text-white transition-colors" aria-label="Schließen"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          <div>
            <label className={labelCls}>Stand</label>
            <div className="flex flex-wrap gap-2">
              {FOLLOW_UP_OUTCOMES.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => chooseOutcome(o.key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    outcome === o.key ? 'bg-blue-600 text-white shadow' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}><MessageSquare className="w-3 h-3 inline mr-1 -mt-0.5" />Was ist der Stand?</label>
            <textarea
              autoFocus
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z. B. Mit Herrn Meier telefoniert, Angebot liegt der Geschäftsführung vor, Entscheidung Ende KW 38."
              className={`${inputCls} resize-y`}
            />
            {!noteOk && <p className="text-[11px] text-slate-400 mt-1">Pflichtfeld — ohne Stand ist der Eintrag im Verlauf wertlos.</p>}
          </div>

          <div>
            <label className={labelCls}><User className="w-3 h-3 inline mr-1 -mt-0.5" />Nachgefasst von</label>
            <input value={byName} onChange={(e) => setByName(e.target.value)} className={inputCls} placeholder="Name" />
          </div>

          <div>
            <label className={labelCls}><Clock className="w-3 h-3 inline mr-1 -mt-0.5" />Nächstes Nachfassen</label>
            <div className="flex gap-2 mb-3">
              {NEXT_MODES.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => chooseMode(key)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    mode === key ? 'bg-slate-900 text-white shadow' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {mode === 'days' && (
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 1))}
                  className={`${inputCls} w-24`}
                />
                <span className="text-sm text-slate-500">Tage</span>
                {QUICK_DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDays(d)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all ${
                      days === d ? 'bg-blue-100 text-blue-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                    }`}
                  >
                    {d} T
                  </button>
                ))}
              </div>
            )}

            {mode === 'date' && (
              <input type="date" value={date} min={todayIso()} onChange={(e) => setDate(e.target.value)} className={`${inputCls} w-48`} />
            )}

            <p className="text-xs text-slate-500 mt-2">
              {nextDate
                ? <>Erneut nachfassen am <strong className="text-slate-700">{fmtDate(nextDate)}</strong>.</>
                : <>Das Angebot verlässt den Reiter <strong className="text-slate-700">dauerhaft</strong> — es wird nicht mehr nachgefasst.</>}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 shrink-0">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Abbrechen</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!canSave}
            icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          >
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
};
